import * as React from 'react';
import { Formik, FormikConfig } from 'formik';

import { Redirect } from 'react-router-dom';
import { makeValidator } from 'mandle';

import { makeInputPropsMaker } from '../../../utils/inputPropsMaker';

import {
  FullMatchFragment,
  useUpdateMatchMutation,
  UpdateMatchInput,
} from '../../../generated/graphql';
import { Input } from '../../../components/Input';
import { SubmitButton } from '../../../components/SubmitButton';
import { formatErrors } from '../../../utils/formatErrors';
import { useAnalytics } from '../../../contexts/AnalyticsContext';
import { useAlert } from '../../../contexts/AlertContext';
import { ButtonLink } from '../../../components/Button';
import { Heading } from '../../../components/Heading';
import { validateInteger, required2 } from '../../../validators';
import { paths } from '../../../paths';
import { isNil } from '../../../utils/isNil';

interface Props {
  match: FullMatchFragment;
}

interface Fields {
  goalsAgainst: number | '';
  goalsFor: number | '';
}

const validateMatchScore = makeValidator({
  goalsFor: [required2('Enter a score'), validateInteger],
  goalsAgainst: [required2('Enter a score'), validateInteger],
});

export const MatchScore: React.FC<Props> = ({ match }) => {
  const analytics = useAnalytics();
  const alert = useAlert();
  const [scoreSubmitted, setScoreSubmitted] = React.useState(false);

  const initialValues: Fields = {
    goalsFor: isNil(match.goalsFor) ? '' : match.goalsFor,
    goalsAgainst: isNil(match.goalsAgainst) ? '' : match.goalsAgainst,
  };

  const [, updateMatch] = useUpdateMatchMutation();

  const sanitizeFields = (fields: Fields): UpdateMatchInput => {
    return {
      goalsFor: fields.goalsFor === '' ? null : fields.goalsFor,
      goalsAgainst: fields.goalsAgainst === '' ? null : fields.goalsAgainst,
    };
  };

  const submitScore: FormikConfig<Fields>['onSubmit'] = (form, actions) => {
    const formEventMeta = { form: 'edit_stats' };

    return updateMatch({
      id: match.id,
      input: sanitizeFields(form),
    }).then((response) => {
      if (response.data?.updateMatch?.validation) {
        analytics.track('Data Submit Failed', formEventMeta);
        return actions.setErrors(
          formatErrors(response.data.updateMatch.validation),
        );
      }
      if (response.data?.updateMatch?.entity) {
        analytics.track('Data Submit Succeeded', formEventMeta);
        setScoreSubmitted(true);
        alert('Score saved 🎉');
      }
    });
  };

  if (scoreSubmitted) {
    return <Redirect to={paths.match.home(match.id)} />;
  }

  return (
    <Formik
      enableReinitialize={true}
      initialValues={initialValues}
      onSubmit={submitScore}
      validate={validateMatchScore}
    >
      {(formProps) => {
        const getInputProps = makeInputPropsMaker<Fields>(formProps);
        return (
          <form onSubmit={formProps.handleSubmit} className="space-y-4">
            <Heading>Match Score</Heading>
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Goals For"
                type="number"
                {...getInputProps('goalsFor')}
              />
              <Input
                label="Goals Against"
                type="number"
                {...getInputProps('goalsAgainst')}
              />
            </div>
            <SubmitButton loading={false}>Save score</SubmitButton>
            <ButtonLink
              to={paths.match.home(match.id)}
              kind="plain"
              icon="arrow-left"
            >
              Back
            </ButtonLink>
          </form>
        );
      }}
    </Formik>
  );
};
